import { FC } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Avatar,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StarIcon from '@mui/icons-material/Star';
import { IProjectUI } from 'data/types/project.types';
import { useRemoveProjectMemberMutation } from 'redux/apiSlices/project.slice';
import { useSnackbar } from 'notistack';
import { errorMessages, successMessages } from 'data/messages/messages';
import { TRtkErrorType } from 'data/types/general.types';
import { EUserRole } from 'shared/types/generic.types';
import AlertDialogWithState from 'components/Dialogs/AlertDialog/AlertDialogWithState';

type TManageProjectMembersListProps = {
  members: IProjectUI['members'];
  projectId: string;
  projectName: string;
};

const ManageProjectMembersList: FC<TManageProjectMembersListProps> = ({
  members,
  projectId,
  projectName,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [removeMember, { isLoading }] = useRemoveProjectMemberMutation();

  const handleDelete = async (userId: string) => {
    try {
      await removeMember({ projectId, userId }).unwrap();
      enqueueSnackbar(successMessages.toast, {
        variant: 'success',
      });
    } catch (error) {
      const e = error as TRtkErrorType;
      enqueueSnackbar(e?.data?.message || errorMessages.default, {
        variant: 'error',
      });
    }
  };

  if (members.length === 0) {
    return (
      <Box
        sx={{
          py: 3,
          textAlign: 'center',
          color: 'text.secondary',
        }}
      >
        <Typography variant="body2">No members yet</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {members.map(member => {
        const isOwner = member.role === EUserRole.OWNER;

        return (
          <Box
            key={member.userId}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 1.5,
              borderRadius: 2,
              backgroundColor: 'background.default',
              transition: 'background-color 0.2s',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
              '&:hover .delete-btn': {
                opacity: 1,
              },
            }}
          >
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: member.avatarColor || 'primary.main',
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              {member.firstLetters}
            </Avatar>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {member.name || member.email.split('@')[0]}
                </Typography>
                {isOwner && (
                  <Chip
                    icon={<StarIcon sx={{ fontSize: 14 }} />}
                    label="Owner"
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      backgroundColor: 'warning.light',
                      color: 'warning.dark',
                      '& .MuiChip-icon': {
                        color: 'warning.dark',
                      },
                    }}
                  />
                )}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {member.email}
              </Typography>
            </Box>

            {!isOwner && (
              <AlertDialogWithState
                title="Remove person"
                text={`Are you sure you want to remove ${member.name || member.email} from ${projectName}?`}
                submitDisabled={isLoading}
                submitLoading={isLoading}
                onConfirm={() => handleDelete(member.userId)}
              >
                <Tooltip title="Remove from project">
                  <IconButton
                    size="small"
                    className="delete-btn"
                    sx={{
                      opacity: 0,
                      transition: 'opacity 0.2s',
                      color: 'text.secondary',
                      '&:hover': {
                        color: 'error.main',
                        backgroundColor: 'error.light',
                      },
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </AlertDialogWithState>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default ManageProjectMembersList;
